<!--
  Chips de identificação do ativo. Cor derivada do texto pelo mesmo
  `stringToColor` das tags do CRM, então o mesmo valor pinta igual em toda a
  plataforma. Cada chip carrega o rótulo do que ele é no tooltip, e os de texto
  livre (classe e instituição) truncam com o valor inteiro no tooltip.
-->
<script>
import { stringToColor } from '@modules/global/utils/Color'
import {
  CHIP_BG_ALPHA,
  CHIP_LIGHTNESS,
  CHIP_SATURATION,
} from '@modules/consolidator/constants/AssetChips'

export default {
  name: 'AssetHeader',
  props: {
    asset: {
      type: Object,
      required: true,
    },
    ctx: {
      type: Object,
      required: true,
    },
  },
  computed: {
    classe() {
      if (this.ctx.isClient) {
        return (
          this.asset['subclasse'] ||
          this.asset['CI'] ||
          this.asset.ci ||
          this.asset.classe ||
          this.asset.Classe ||
          ''
        )
      }
      return (
        this.asset['CI'] ||
        this.asset.ci ||
        this.asset.classe ||
        this.asset.Classe ||
        ''
      )
    },
    chips() {
      const asset = this.asset
      const items = [
        {
          key: 'tipo',
          rotulo: 'Tipo de investimento',
          texto: asset['Tipo'] || asset.tipo,
          colorido: true,
          truncar: false,
        },
        {
          key: 'classe',
          rotulo: this.ctx.isClient ? 'Subclasse' : 'Classe',
          texto: this.classe,
          colorido: true,
          truncar: true,
        },
        {
          key: 'instituicao',
          rotulo: 'Instituição',
          texto: asset['Corretora'] || asset.corretora,
          colorido: true,
          truncar: true,
        },
        {
          key: 'isin',
          rotulo: 'ISIN',
          texto: asset.isin || null,
          colorido: false,
          truncar: false,
        },
      ]
      return items
        .filter((item) => Boolean(item.texto))
        .map((item) => ({
          ...item,
          estilo: this.estilo(item),
          dica: this.dica(item),
        }))
    },
  },
  methods: {
    estilo(chip) {
      if (!chip.colorido) {
        return {}
      }
      const cor = stringToColor(chip.texto, {
        format: 'object',
        saturation: CHIP_SATURATION,
        lightness: CHIP_LIGHTNESS,
      })
      if (!cor || typeof cor.r !== 'number') {
        return {}
      }
      return {
        color: `rgb(${cor.r}, ${cor.g}, ${cor.b})`,
        background: `rgba(${cor.r}, ${cor.g}, ${cor.b}, ${CHIP_BG_ALPHA})`,
      }
    },
    dica(chip) {
      return `${chip.rotulo}: ${chip.texto}`
    },
  },
}
</script>

<template>
  <div class="asset-header">
    <div class="asset-header__chips">
      <v-tooltip v-for="chip in chips" :key="chip.key" bottom open-delay="300">
        <template v-slot:activator="{ on, attrs }">
          <span
            class="asset-header__chip"
            :class="{ 'asset-header__chip--truncado': chip.truncar }"
            :style="chip.estilo"
            v-bind="attrs"
            v-on="on"
          >
            {{ chip.texto }}
          </span>
        </template>
        <span>{{ chip.dica }}</span>
      </v-tooltip>
    </div>
  </div>
</template>

<style lang="sass" scoped>
.asset-header
  &__chips
    display: flex
    flex-wrap: wrap
    gap: 8px

  // O `v-tooltip` renderiza um wrapper 0x0 que ainda conta como item de flex e
  // dobraria o `gap` em volta de cada chip.
  ::v-deep .v-tooltip
    display: contents

  &__chip
    font-size: 12px
    font-weight: 600
    color: var(--secondary)
    background: color-mix(in srgb, var(--secondary) 12%, transparent)
    border-radius: 999px
    padding: 4px 12px
    white-space: nowrap
    display: inline-block

    &--truncado
      max-width: 220px
      overflow: hidden
      text-overflow: ellipsis
</style>
